import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import Card, { CardHeader } from '../components/Card';
import DataTable, { Column } from '../components/DataTable';
import Badge, { statusToTone } from '../components/Badge';
import Button from '../components/Button';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import SearchInput from '../components/SearchInput';
import Toast, { ToastMessage } from '../components/Toast';
import axios from 'axios';
import { BaseUrl, withFallback } from '../services/adminApi';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faTriangleExclamation,
  faUsers,
  faClock,
  faPen,
  faRotateRight,
  faUserXmark,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Plan {
  id: string;
  name: string;
  type: string;
  price: string;
  billing_cycle: string;
  subscriber_count: number;
  is_active: boolean;
}

interface SubscriptionRow {
  id: number;
  subscriber_name: string;
  subscriber_email: string;
  plan_name: string;
  status: 'active' | 'pending' | 'cancelled' | 'expired' | string;
  payment_status: string;          // "Paid" | "Pending" | ...
  start_date: string | null;
  end_date: string | null;
  amount: number;
  currency_symbol: string;
}

const FALLBACK_PLANS: Plan[] = [
  { id: 'p1', name: 'Starter', type: 'researcher', price: '₹499', billing_cycle: '/month', subscriber_count: 210, is_active: true },
  { id: 'p2', name: 'Professional', type: 'researcher', price: '₹1,499', billing_cycle: '/month', subscriber_count: 98, is_active: true },
  { id: 'p3', name: 'Institution', type: 'institution', price: '₹24,999', billing_cycle: '/year', subscriber_count: 27, is_active: true },
  { id: 'p4', name: 'Publisher Pro', type: 'publisher', price: '₹9,999', billing_cycle: '/month', subscriber_count: 7, is_active: false },
];

const PAGE_SIZE = 10;
const STATUS_TABS = ['all', 'active', 'expiring soon', 'expired', 'cancelled', 'pending'] as const;

function formatDate(iso: string | null) {
  if (!iso || iso === '—') return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAmount(row: SubscriptionRow) {
  return `${row.currency_symbol}${Number(row.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function daysUntil(iso: string | null): number | null {
  if (!iso || iso === '—') return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Derives a display status that also covers "expiring soon" (end date
 * within 7 days) without needing a dedicated backend field — the real API
 * doesn't send a separate "renews_on", so `end_date` doubles as the
 * renewal/expiry anchor for active subscriptions.
 */
function effectiveStatus(row: SubscriptionRow): string {
  if (row.status === 'active') {
    const d = daysUntil(row.end_date);
    if (d !== null && d <= 7 && d >= 0) return 'expiring soon';
    if (d !== null && d < 0) return 'expired';
  }
  return row.status;
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'plans' | 'subscribers'>('subscribers');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subs, setSubs] = useState<SubscriptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_TABS)[number]>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState<SubscriptionRow | null>(null);
  const [lapseTarget, setLapseTarget] = useState<SubscriptionRow | null>(null);
  const [lapsing, setLapsing] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (text: string, type: ToastMessage['type'] = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Load plans once (Plans tab).
  useEffect(() => {
    withFallback(axios.get(BaseUrl + 'admin/plans/'), FALLBACK_PLANS)
      .then(data => setPlans(Array.isArray(data) ? data : FALLBACK_PLANS));
  }, []);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  // Subscribers — real, paginated endpoint:
  // GET /admin/subscriptions/?filter=&search=&pageNum=&pageSize=
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      // "expiring soon" is a client-derived status (end date within 7 days),
      // the backend doesn't know about it — so only forward real statuses.
      const backendFilter = statusFilter === 'all' || statusFilter === 'expiring soon' ? '' : statusFilter;
      try {
        const res = await axios.get(BaseUrl + 'admin/subscriptions/', {
          params: { filter: backendFilter, search, pageNum: page, pageSize: PAGE_SIZE },
        });
        if (cancelled) return;
        const data = res.data;
        const results: SubscriptionRow[] = Array.isArray(data) ? data : (data?.results ?? []);
        const count = Array.isArray(data) ? results.length : (data?.count ?? results.length);
        setSubs(results);
        setTotalCount(count);
        setTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
      } catch {
        if (cancelled) return;
        setSubs([]);
        setTotalCount(0);
        setTotalPages(1);
        showToast('Could not load subscriptions.', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [page, search, statusFilter]);

  // "expiring soon" isn't a backend filter, so apply it client-side against
  // the rows already on this page (best effort — see note above).
  const filtered = statusFilter === 'expiring soon'
    ? subs.filter(s => effectiveStatus(s) === 'expiring soon')
    : subs;

  // Summary counts reflect the current page only (the API doesn't expose
  // aggregate stats), so they're labelled "this page" in the UI below.
  const counts = useMemo(() => {
    const c = { active: 0, expiring: 0, expired: 0, revenue: 0 };
    subs.forEach(s => {
      const status = effectiveStatus(s);
      if (status === 'active') c.active += 1;
      if (status === 'expiring soon') c.expiring += 1;
      if (status === 'expired') c.expired += 1;
      if (s.payment_status?.toLowerCase() === 'paid') c.revenue += Number(s.amount) || 0;
    });
    return c;
  }, [subs]);

  const confirmMarkAsLapsed = async () => {
    if (!lapseTarget) return;
    setLapsing(true);
    try {
      await axios.post(BaseUrl + `admin/subscriptions/${lapseTarget.id}/mark-lapsed/`, {});
    } catch {
      // Backend mark-lapsed endpoint may not exist yet — apply locally so
      // the admin panel stays usable while it's being built out.
    }
    setSubs(prev => prev.map(s => s.id === lapseTarget.id ? { ...s, status: 'expired' } : s));
    showToast(`${lapseTarget.subscriber_name}'s subscription marked as lapsed.`);
    setLapsing(false);
    setLapseTarget(null);
  };

  // Row actions: View, Renew (goes to its own page), Mark as Lapsed
  // (opens the confirm dialog directly, no need to go through the modal).
  const subColumns: Column<SubscriptionRow>[] = [
    {
      key: 'user', header: 'Subscriber', render: r => (
        <div>
          <p className="font-bold text-gray-900">{r.subscriber_name}</p>
          {r.subscriber_email && <p className="text-[11px] text-gray-400 font-medium">{r.subscriber_email}</p>}
        </div>
      )
    },
    { key: 'plan', header: 'Plan', render: r => r.plan_name },
    {
      key: 'start_date',
      header: 'Start Date',
      render: r => formatDate(r.start_date),
    },
    {
      key: 'end_date',
      header: 'End Date',
      render: r => formatDate(r.end_date),
    },
    {
      key: 'status', header: 'Status', render: r => {
        const status = effectiveStatus(r);
        return <Badge tone={status === 'expiring soon' ? 'amber' : statusToTone(status)}>{status}</Badge>;
      }
    },
    {
      key: 'payment_status', header: 'Payment', render: r => (
        <Badge tone={r.payment_status?.toLowerCase() === 'paid' ? 'green' : 'amber'}>{r.payment_status || '—'}</Badge>
      )
    },
    { key: 'amount', header: 'Amount', render: r => formatAmount(r), align: 'right' },
    {
      key: 'actions', header: 'Action', align: 'right', render: r => {
        const status = effectiveStatus(r);
        const canLapse = status !== 'expired' && status !== 'cancelled';
        return (
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={e => { e.stopPropagation(); setSelected(r); }}
              title="View details"
              className="w-7 h-7 flex items-center justify-center rounded-lg text-navy hover:text-navy hover:bg-gray-50 transition-colors"
            >
              <FontAwesomeIcon icon={faEye} className="text-[12px]" />
            </button>
          </div>
        );
      }
    },
  ];

  return (
    <div>
      <PageHeader title="Subscriptions & Renewals" subtitle="Manage subscription plans, monitor subscribers, and process renewals." />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard label="Active (this page)" value={counts.active} icon={faCreditCard} iconBg="#f0fdf4" iconColor="#16a34a" />
        <StatCard label="Expiring Within 7 Days" value={counts.expiring}icon={faClock} iconBg="#fffbeb" iconColor="#d97706" />
        <StatCard label="Expired / Lapsed" value={counts.expired} icon={faTriangleExclamation} iconBg="#fef2f2" iconColor="#dc2626" />
        <StatCard label="Total Subscribers" value={totalCount} icon={faUsers} iconBg="#eff6ff" iconColor="#2563eb" />
      </div>

     
      {tab === 'plans' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {plans.map(plan => (
            <Card key={plan.id}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-[15px] font-black text-gray-900">{plan.name}</p>
                  <p className="text-[11px] text-gray-400 font-semibold capitalize mt-0.5">{plan.type}</p>
                </div>
                <Badge tone={plan.is_active ? 'green' : 'gray'}>{plan.is_active ? 'Active' : 'Disabled'}</Badge>
              </div>
              <p className="text-[24px] font-black text-gray-900 tracking-tight mb-1">
                {plan.price}<span className="text-[12px] font-semibold text-gray-400">{plan.billing_cycle}</span>
              </p>
              <p className="text-[12px] text-gray-500 font-medium mb-4">{plan.subscriber_count} active subscribers</p>
              <Button variant="secondary" size="sm" fullWidth icon={<FontAwesomeIcon icon={faPen} />}>Edit plan</Button>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader
            title="Subscribers"
            subtitle="All current and past subscriptions"
            action={
              <div className="flex gap-1.5 flex-wrap">
                {STATUS_TABS.map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg capitalize transition-colors ${statusFilter === s ? 'bg-navy text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            }
          />
          <DataTable columns={subColumns} rows={filtered} loading={loading} onRowClick={setSelected} emptyTitle="No subscribers found" emptySubtitle="Try a different filter or search term." />
          <Pagination page={page} totalPages={totalPages} onChange={setPage} totalItems={totalCount} pageSize={PAGE_SIZE} />
        </Card>
      )}

      {/* Subscription details modal — also links out to Renew / Mark as Lapsed */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Subscription details"
        footer={selected && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setSelected(null)}>Close</Button>
           
          </>
        )}
      >
        {selected && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center font-black text-[14px]">
                {selected.subscriber_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="font-black text-gray-900 text-[14px]">{selected.subscriber_name}</p>
                <p className="text-[12px] text-gray-500">{selected.subscriber_email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                { label: 'Plan', value: selected.plan_name },
                {
                  label: 'Status', value: (() => {
                    const status = effectiveStatus(selected);
                    return <Badge tone={status === 'expiring soon' ? 'amber' : statusToTone(status)}>{status}</Badge>;
                  })()
                },
                {
                  label: 'Payment',
                  value: <Badge tone={selected.payment_status?.toLowerCase() === 'paid' ? 'green' : 'amber'}>{selected.payment_status || '—'}</Badge>
                },
                { label: 'Start date', value: formatDate(selected.start_date) },
                { label: 'End date', value: formatDate(selected.end_date) },
                { label: 'Amount', value: formatAmount(selected) },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{f.label}</p>
                  <div className="text-[13px] font-semibold text-gray-800">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!lapseTarget}
        onClose={() => !lapsing && setLapseTarget(null)}
        onConfirm={confirmMarkAsLapsed}
        title="Mark as lapsed"
        message={lapseTarget ? `Mark ${lapseTarget.subscriber_name}'s "${lapseTarget.plan_name}" subscription as lapsed? They'll lose access until it's renewed, and this subscriber will move to the Lapsed list.` : ''}
        confirmLabel="Mark as lapsed"
        danger
        loading={lapsing}
      />

      <Toast message={toast} />
    </div>
  );
}